import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import i18next from 'i18next';

import { isNotFound } from '../api/apiError';
import { AuthApi } from '../api/authApi';
import { UserListResponse } from '../api/commonResponse';
import { User } from '../model/user';
import { RootState } from '../rootReducer';
import { SetUserId } from '../utils/gtag';
import { setSigninError, withTopError } from './errorSlice';
import { ThunkConfig } from './thunkConfig';

export const login = createAsyncThunk<UserListResponse, void, ThunkConfig>(`user/login`, async (_, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    try {
        const user = await AuthApi.login();
        SetUserId(user.id);
        return user;
    } catch (e) {
        // when user is not found (cookie is expire or cookie isn't set, yet), don't show error
        if (!isNotFound(e)) {
            dispatch(setSigninError(i18next.t('error.login')));
        }
        throw e;
    }
});

export const logout = createAsyncThunk(
    `user/logout`,
    withTopError(async () => await AuthApi.logout(), i18next.t('error.logout'))
);

export type UserState = {
    entity: User | null;
    isAuth: boolean | undefined;
};

const initialState: UserState = {
    entity: null,
    isAuth: undefined,
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(login.fulfilled, (state, action) => {
            state.entity = {
                id: action.payload.id,
                name: action.payload.name,
                image: action.payload.user_image,
            };
            state.isAuth = true;
        });
        builder.addCase(login.rejected, (state) => {
            state.isAuth = false;
        });
        builder.addCase(logout.fulfilled, (state) => {
            state.isAuth = false;
            state.entity = null;
        });
    },
});

export const selectUser = (state: RootState): UserState => state.user;

export const userReducer = userSlice.reducer;
